import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  LinearProgress
} from '@mui/material';
import { Add, Edit, Delete, Close, CloudUpload } from '@mui/icons-material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

const emptyStep = { label: '', content: '', mediaType: 'text', mediaUrl: '' };
const emptyGuide = { title: '', steps: [ { ...emptyStep } ] };

const FirstAidGuideForm = () => {
  const [guides, setGuides] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentGuide, setCurrentGuide] = useState(emptyGuide);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingStepIdx, setUploadingStepIdx] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch guides from backend
  const fetchGuides = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/first-aid-guides');
      setGuides(res.data);
    } catch (err) {
      setError('Failed to load guides');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuides();
  }, []);

  const handleOpenAdd = () => {
    setCurrentGuide(emptyGuide);
    setEditMode(false);
    setOpen(true);
  };

  const handleOpenEdit = (guide) => {
    setCurrentGuide({ ...guide, steps: guide.steps.map(s => ({ ...emptyStep, ...s })) });
    setSelectedId(guide.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentGuide(emptyGuide);
    setSelectedId(null);
    setUploadingStepIdx(null);
    setUploadProgress(0);
  };

  const handleChange = (e) => {
    setCurrentGuide({ ...currentGuide, [e.target.name]: e.target.value });
  };

  const handleStepChange = (idx, field, value) => {
    const steps = [...currentGuide.steps];
    steps[idx][field] = value;
    // If mediaType is changed, reset content/mediaUrl accordingly
    if (field === 'mediaType') {
      if (value === 'text') {
        steps[idx].content = '';
        steps[idx].mediaUrl = '';
      } else {
        steps[idx].content = '';
        steps[idx].mediaUrl = '';
      }
    }
    setCurrentGuide({ ...currentGuide, steps });
  };

  const handleAddStep = () => {
    setCurrentGuide({ ...currentGuide, steps: [...currentGuide.steps, { ...emptyStep }] });
  };

  const handleRemoveStep = (idx) => {
    const steps = currentGuide.steps.filter((_, i) => i !== idx);
    setCurrentGuide({ ...currentGuide, steps });
  };

  const handleFileUpload = async (idx, file) => {
    setUploadingStepIdx(idx);
    setUploadProgress(0);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/first-aid-guides/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      handleStepChange(idx, 'mediaUrl', res.data.url);
    } catch (err) {
      setError('Failed to upload file');
    } finally {
      setUploadingStepIdx(null);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editMode) {
        await axios.put(`/first-aid-guides/${selectedId}`, currentGuide);
        setGuides(guides.map(g => (g.id === selectedId ? { ...currentGuide, id: selectedId } : g)));
      } else {
        const res = await axios.post('/first-aid-guides', currentGuide);
        setGuides([...guides, res.data]);
      }
      handleClose();
    } catch (err) {
      setError('Failed to save guide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    setError(null);
    try {
      await axios.delete(`/first-aid-guides/${id}`);
      setGuides(guides.filter(g => g.id !== id));
    } catch (err) {
      setError('Failed to delete guide');
    } finally {
      setSaving(false);
    }
  };

  // Helper for rendering step content/media
  const renderStepContent = (step) => {
    if (step.mediaType === 'image' && step.mediaUrl) {
      // Construct the full URL for the image
      const fullImageUrl = step.mediaUrl.startsWith('http') 
        ? step.mediaUrl 
        : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${step.mediaUrl}`;
      
      console.log('Rendering image with URL:', fullImageUrl);
      return (
        <Box sx={{ 
          position: 'relative',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
          <img 
            src={fullImageUrl} 
            alt="First Aid Step" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: 160, 
              objectFit: 'cover',
              display: 'block'
            }}
            onError={(e) => {
              console.error('Image failed to load:', fullImageUrl);
              e.target.style.display = 'none';
            }}
            onLoad={() => {
              console.log('Image loaded successfully:', fullImageUrl);
            }}
          />
          <Box sx={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            background: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '0.25rem',
            padding: '0.25rem 0.5rem',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            🖼️ Image
          </Box>
        </Box>
      );
    }
    if (step.mediaType === 'video' && step.mediaUrl) {
      // Construct the full URL for the video
      const fullVideoUrl = step.mediaUrl.startsWith('http') 
        ? step.mediaUrl 
        : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${step.mediaUrl}`;
      
      console.log('Rendering video with URL:', fullVideoUrl);
      return (
        <Box sx={{ 
          position: 'relative',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
          <video 
            controls 
            style={{ 
              maxWidth: '100%', 
              maxHeight: 180,
              objectFit: 'cover',
              display: 'block'
            }}
            onError={(e) => {
              console.error('Video failed to load:', fullVideoUrl);
            }}
            onLoad={() => {
              console.log('Video loaded successfully:', fullVideoUrl);
            }}
          >
            <source src={fullVideoUrl} />
            Your browser does not support the video tag.
          </video>
          <Box sx={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            background: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '0.25rem',
            padding: '0.25rem 0.5rem',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            🎬 Video
          </Box>
        </Box>
      );
    }
    // Default to text
    return (
      <Typography sx={{ 
        fontSize: '0.875rem',
        lineHeight: 1.6,
        color: '#374151'
      }}>
        {step.content}
      </Typography>
    );
  };

  

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '1.5rem',
      '& *': {
        fontFamily: '"Inter", "Segoe UI", sans-serif !important'
      }
    }}>
      <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header Section */}
        <Box sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '1rem',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              borderRadius: '50%',
              padding: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 16px rgba(220, 38, 38, 0.3)'
            }}>
              <Box sx={{ fontSize: '1.25rem' }}>🚑</Box>
            </Box>
            <Box>
              <Typography sx={{ 
                fontSize: '1.5rem', 
                fontWeight: '700',
                background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: 0
              }}>
                First Aid Guide Manager
              </Typography>
              <Typography sx={{ 
                color: '#64748b', 
                fontSize: '0.85rem',
                margin: 0
              }}>
                Create and manage emergency response guides
              </Typography>
            </Box>
          </Box>
          
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={handleOpenAdd}
            sx={{
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              borderRadius: '0.5rem',
              padding: '0.625rem 1.25rem',
              textTransform: 'none',
              fontWeight: '600',
              fontSize: '0.875rem',
              boxShadow: '0 8px 16px rgba(220, 38, 38, 0.3)',
              border: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 20px rgba(220, 38, 38, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Add First Aid Guide
          </Button>
        </Box>

        {/* Main Content Card */}
        <Card sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '1.5rem',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ padding: '2rem' }}>
            <Typography sx={{ 
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📋 First Aid Guides
            </Typography>
            
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                padding: '4rem',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <CircularProgress sx={{ color: '#dc2626' }} />
                <Typography sx={{ color: '#64748b' }}>Loading guides...</Typography>
              </Box>
            ) : error ? (
              <Alert 
                severity="error" 
                sx={{ 
                  borderRadius: '0.75rem',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  border: '1px solid #fecaca',
                  '& .MuiAlert-icon': { color: '#dc2626' }
                }}
              >
                {error}
              </Alert>
            ) : (
              <List sx={{ padding: 0 }}>
                {guides.map((guide, index) => (
                  <React.Fragment key={guide.id}>
                    <ListItem sx={{
                      background: index % 2 === 0 
                        ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                        : 'linear-gradient(135deg, #fefefe 0%, #f9fafb 100%)',
                      borderRadius: '1rem',
                      marginBottom: '1rem',
                      padding: '1.5rem',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)'
                      }
                    }}>
                      <ListItemText
                        primary={
                          <Typography sx={{
                            fontWeight: '600',
                            fontSize: '1.1rem',
                            color: '#1e293b',
                            marginBottom: '1rem'
                          }}>
                            {guide.title}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {guide.steps.map((step, idx) => (
                              <Box 
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  gap: '0.75rem',
                                  alignItems: 'flex-start',
                                  padding: '0.75rem',
                                  background: 'rgba(255, 255, 255, 0.7)',
                                  borderRadius: '0.5rem',
                                  border: '1px solid rgba(226, 232, 240, 0.5)'
                                }}
                              >
                                <Typography sx={{
                                  background: `linear-gradient(135deg, ${
                                    idx < 2 ? '#dc2626' : idx < 4 ? '#f59e0b' : '#059669'
                                  } 0%, ${
                                    idx < 2 ? '#ef4444' : idx < 4 ? '#fbbf24' : '#10b981'
                                  } 100%)`,
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem',
                                  flexShrink: 0,
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}>
                                  {step.label}
                                </Typography>
                                {renderStepContent(step)}
                              </Box>
                            ))}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={1}>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleOpenEdit(guide)} 
                            disabled={saving}
                            sx={{
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              color: 'white',
                              borderRadius: '0.5rem',
                              padding: '0.5rem',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                transform: 'scale(1.05)'
                              },
                              '&:disabled': {
                                background: '#e2e8f0',
                                color: '#94a3b8'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Edit size={16} />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleDelete(guide.id)} 
                            disabled={saving}
                            sx={{
                              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                              color: 'white',
                              borderRadius: '0.5rem',
                              padding: '0.5rem',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
                                transform: 'scale(1.05)'
                              },
                              '&:disabled': {
                                background: '#e2e8f0',
                                color: '#94a3b8'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Delete size={16} />
                          </IconButton>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Dialog/Modal */}
        <Dialog 
          open={open} 
          onClose={handleClose} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '1rem',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              maxHeight: '85vh',
              maxWidth: '600px'
            }
          }}
          BackdropProps={{
            sx: {
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(8px)'
            }
          }}
        >
          <DialogTitle sx={{
            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            color: 'white',
            padding: '1.25rem',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              pointerEvents: 'none'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', position: 'relative', zIndex: 1 }}>
              <Box sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                🚑
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                  {editMode ? 'Edit First Aid Guide' : 'Add First Aid Guide'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', opacity: 0.9, margin: 0 }}>
                  {editMode ? 'Update guide information' : 'Create emergency response guide'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                width: '32px',
                height: '32px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              <Close size={16} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ padding: '1.25rem', background: '#fafafa' }}>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  marginBottom: '1rem',
                  borderRadius: '0.5rem',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  border: '1px solid #fecaca',
                  fontSize: '0.875rem'
                }}
              >
                {error}
              </Alert>
            )}
            
            <TextField
              label="Guide Title"
              name="title"
              value={currentGuide.title}
              onChange={handleChange}
              fullWidth
              margin="normal"
              disabled={saving}
              size="small"
              sx={{
                marginBottom: '1rem',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.5rem',
                  background: 'white',
                  fontSize: '0.875rem',
                  '&:hover fieldset': {
                    borderColor: '#dc2626'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#dc2626'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#dc2626'
                }
              }}
            />
            
            <Typography sx={{ 
              fontSize: '0.95rem', 
              fontWeight: '600', 
              marginBottom: '0.75rem',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📝 Steps
            </Typography>
            
            <Stack spacing={1.5}>
              {currentGuide.steps.map((step, idx) => (
                <Box 
                  key={idx} 
                  sx={{ 
                    display: 'flex', 
                    gap: '0.75rem', 
                    alignItems: 'flex-start',
                    padding: '0.75rem',
                    background: 'white',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <TextField
                    label="Label"
                    value={step.label}
                    onChange={e => handleStepChange(idx, 'label', e.target.value)}
                    size="small"
                    sx={{ 
                      width: '80px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        '&:hover fieldset': {
                          borderColor: '#dc2626'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#dc2626'
                        }
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#dc2626'
                      }
                    }}
                    disabled={saving}
                  />
                  <FormControl sx={{ minWidth: 100 }} size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={step.mediaType || 'text'}
                      label="Type"
                      onChange={e => handleStepChange(idx, 'mediaType', e.target.value)}
                      disabled={saving}
                    >
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="image">Image</MenuItem>
                      <MenuItem value="video">Video</MenuItem>
                    </Select>
                  </FormControl>
                  {step.mediaType === 'text' && (
                    <TextField
                      label="Content"
                      value={step.content}
                      onChange={e => handleStepChange(idx, 'content', e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      disabled={saving}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          '&:hover fieldset': {
                            borderColor: '#dc2626'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#dc2626'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#dc2626'
                        }
                      }}
                    />
                  )}
                  {(step.mediaType === 'image' || step.mediaType === 'video') && (
  <Box sx={{ 
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  }}>
    {/* Upload Area */}
    <Box
      sx={{
        border: step.mediaUrl ? '2px solid #10b981' : '2px dashed #cbd5e1',
        borderRadius: '0.75rem',
        padding: '1rem',
        background: step.mediaUrl 
          ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        textAlign: 'center',
        cursor: uploadingStepIdx === idx ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': uploadingStepIdx !== idx ? {
          borderColor: step.mediaType === 'image' ? '#3b82f6' : '#8b5cf6',
          background: step.mediaType === 'image' 
            ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
            : 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)'
        } : {}
      }}
      onClick={() => !uploadingStepIdx && document.getElementById(`file-upload-${idx}`).click()}
    >
      <input
        type="file"
        accept={step.mediaType === 'image' ? 'image/*' : 'video/*'}
        style={{ display: 'none' }}
        id={`file-upload-${idx}`}
        onChange={async (e) => {
          if (e.target.files && e.target.files[0]) {
            await handleFileUpload(idx, e.target.files[0]);
          }
        }}
        disabled={saving || uploadingStepIdx === idx}
      />
      
      {uploadingStepIdx === idx ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: '0.75rem' 
        }}>
          <CircularProgress 
            size={32} 
            sx={{ 
              color: step.mediaType === 'image' ? '#3b82f6' : '#8b5cf6' 
            }} 
          />
          <Typography sx={{ 
            fontSize: '0.875rem', 
            fontWeight: '600',
            color: '#475569' 
          }}>
            Uploading... {uploadProgress}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress}
            sx={{ 
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                background: step.mediaType === 'image' 
                  ? 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)'
                  : 'linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '3px'
              }
            }}
          />
        </Box>
      ) : step.mediaUrl ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: '0.75rem' 
        }}>
          {step.mediaType === 'image' ? (
            <img 
              src={step.mediaUrl.startsWith('http') 
                ? step.mediaUrl 
                : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${step.mediaUrl}`} 
              alt="Uploaded preview" 
              style={{ 
                maxWidth: '120px', 
                maxHeight: '80px', 
                borderRadius: '0.5rem',
                objectFit: 'cover',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }} 
            />
          ) : (
            <Box sx={{ 
              position: 'relative',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
              <video 
                src={step.mediaUrl.startsWith('http') 
                  ? step.mediaUrl 
                  : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${step.mediaUrl}`} 
                style={{ 
                  maxWidth: '120px', 
                  maxHeight: '80px',
                  objectFit: 'cover'
                }} 
              />
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '50%',
                padding: '0.25rem',
                color: 'white',
                fontSize: '0.75rem'
              }}>
                ▶️
              </Box>
            </Box>
          )}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            background: 'rgba(16, 185, 129, 0.1)',
            padding: '0.25rem 0.75rem',
            borderRadius: '1rem',
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <Box sx={{ fontSize: '0.75rem' }}>✅</Box>
            <Typography sx={{ 
              fontSize: '0.75rem', 
              fontWeight: '600',
              color: '#059669' 
            }}>
              {step.mediaType === 'image' ? 'Image' : 'Video'} uploaded
            </Typography>
          </Box>
          <Typography sx={{ 
            fontSize: '0.75rem', 
            color: '#64748b',
            fontStyle: 'italic'
          }}>
            Click to replace
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: '0.75rem' 
        }}>
          <Box sx={{
            background: step.mediaType === 'image' 
              ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
              : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '50%',
            padding: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
          }}>
            <Box sx={{ fontSize: '1.25rem' }}>
              {step.mediaType === 'image' ? '🖼️' : '🎬'}
            </Box>
          </Box>
          <Typography sx={{ 
            fontSize: '0.875rem', 
            fontWeight: '600',
            color: '#1e293b' 
          }}>
            Upload {step.mediaType === 'image' ? 'Image' : 'Video'}
          </Typography>
          <Typography sx={{ 
            fontSize: '0.75rem', 
            color: '#64748b' 
          }}>
            Click to browse files
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(59, 130, 246, 0.1)',
            padding: '0.25rem 0.75rem',
            borderRadius: '1rem',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <Box sx={{ fontSize: '0.75rem' }}>💡</Box>
            <Typography sx={{ 
              fontSize: '0.75rem', 
              color: '#3b82f6',
              fontWeight: '500'
            }}>
              {step.mediaType === 'image' ? 'JPG, PNG, GIF' : 'MP4, MOV, AVI'}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  </Box>
)}
                  <IconButton 
                    onClick={() => handleRemoveStep(idx)} 
                    disabled={currentGuide.steps.length === 1 || saving || uploadingStepIdx === idx}
                    size="small"
                    sx={{
                      background: currentGuide.steps.length === 1 ? '#f1f5f9' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      color: currentGuide.steps.length === 1 ? '#94a3b8' : 'white',
                      borderRadius: '0.375rem',
                      width: '32px',
                      height: '32px',
                      '&:hover': {
                        background: currentGuide.steps.length === 1 ? '#f1f5f9' : 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
                        transform: currentGuide.steps.length === 1 ? 'none' : 'scale(1.05)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Delete size={14} />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            
            <Button 
              onClick={handleAddStep} 
              startIcon={<Add />} 
              disabled={saving}
              size="small"
              sx={{ 
                marginTop: '0.75rem',
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                color: 'white',
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                textTransform: 'none',
                fontWeight: '600',
                fontSize: '0.875rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Add Step
            </Button>
          </DialogContent>
          
          <DialogActions sx={{ 
            padding: '1rem 1.25rem',
            background: '#f8fafc',
            gap: '0.75rem'
          }}>
            <Button 
              onClick={handleClose} 
              disabled={saving}
              size="small"
              sx={{
                color: '#64748b',
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                textTransform: 'none',
                fontWeight: '600',
                fontSize: '0.875rem',
                border: '1px solid #e2e8f0',
                '&:hover': {
                  background: '#f1f5f9',
                  borderColor: '#cbd5e1'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={saving}
              size="small"
              sx={{
                background: saving 
                  ? '#e2e8f0' 
                  : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                color: saving ? '#94a3b8' : 'white',
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                textTransform: 'none',
                fontWeight: '600',
                fontSize: '0.875rem',
                minWidth: '100px',
                '&:hover': {
                  background: saving 
                    ? '#e2e8f0' 
                    : 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                  transform: saving ? 'none' : 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {saving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CircularProgress size={14} sx={{ color: '#94a3b8' }} />
                  Saving...
                </Box>
              ) : (
                editMode ? 'Update' : 'Create'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default FirstAidGuideForm;